/**
 * File validation utilities
 */

// =============================================================================
// FILE NAME SANITIZATION
// =============================================================================

/**
 * Check if a character is unsafe for URLs or Azure storage
 */
function isUnsafeChar(char: string): boolean {
    // URL / Azure-problematic characters
    if (/[ '`@$#?%&+/\\:]/.test(char)) return true;

    // Control characters (ASCII < 32)
    if (char.charCodeAt(0) < 32) return true;

    return false;
}

/**
 * Sanitize a file name by removing or replacing problematic characters
 * - Normalizes Unicode (é → e)
 * - Removes diacritics
 * - Replaces URL/Azure-problematic characters with underscores
 * - Collapses multiple underscores
 * - Preserves file extension
 *
 * @param fileName - The original file name
 * @returns Sanitized file name safe for URLs and Azure storage
 */
export function sanitizeFileName(fileName: string): string {
    if (!fileName || typeof fileName !== "string") {
        throw new Error("File name must be a non-empty string");
    }

    // Normalize Unicode (é → e + ́)
    const normalized = fileName.normalize("NFD");

    let result = "";
    for (const char of normalized) {
        // Skip diacritics
        if (/\p{Diacritic}/u.test(char)) continue;

        if (isUnsafeChar(char)) {
            result += "_";
        } else {
            result += char;
        }
    }

    // Collapse multiple underscores
    result = result.replace(/_+/g, "_");

    // Preserve extension
    const lastDot = result.lastIndexOf(".");
    let name = lastDot > 0 ? result.slice(0, lastDot) : result;
    const ext = lastDot > 0 ? result.slice(lastDot) : "";

    if (!name.trim()) {
        name = "file";
    }

    return name + ext;
}

/**
 * Create a new File object with a sanitized name
 * File objects are immutable, so we need to create a new one
 *
 * @param file - The original File object
 * @returns A new File object with a sanitized name
 */
export function createSanitizedFile(file: File): File {
    const sanitizedName = sanitizeFileName(file.name);

    // If name didn't change, return original file to avoid unnecessary object creation
    if (sanitizedName === file.name) {
        return file;
    }

    console.log("[SANITIZE] File name changed", {
        original: file.name,
        sanitized: sanitizedName,
    });

    return new File([file], sanitizedName, {
        type: file.type,
        lastModified: file.lastModified,
    });
}

// =============================================================================
// UPLOAD FORMAT ACCEPTANCE (multi-format: PDF + Office)
// =============================================================================
//
// UX-layer mirror of the backend's authoritative FileTypeValidator: the server
// re-validates every upload with magic-byte + OOXML content inspection, so this
// module only needs extension/MIME classification for fast, friendly rejection.

/** Canonical formats accepted by the platform (mirrors backend FileTypeEnum). */
export type UploadFileFormat = "pdf" | "xls" | "xlsx" | "doc" | "docx";

export type UploadFileClassification =
    | { accepted: true; format: UploadFileFormat; isOffice: boolean }
    | { accepted: false; reason: "blocked-macro-enabled" | "unsupported-type" | "not-enabled-for-org"; format?: UploadFileFormat };

/** Backend FileTypeEnum value → canonical format (Unknown=0 has no format). */
export const FILE_TYPE_ID_TO_FORMAT: Record<number, UploadFileFormat> = {
    1: "pdf",
    2: "xls",
    3: "xlsx",
    4: "doc",
    5: "docx",
};

/**
 * File types admins can enable/disable per organization (mirrors backend
 * FileTypeCatalog.ManageableFileTypes). `defaultActive` mirrors the backend's
 * DefaultActiveFileTypes: the toggle state when the org has no configured row
 * (PDF is enabled out of the box). Expanding this list — e.g. adding legacy
 * xls/doc later — is the only frontend change needed.
 */
export const MANAGEABLE_FILE_TYPES: ReadonlyArray<{ id: number; format: UploadFileFormat; label: string; extension: string; defaultActive: boolean }> = [
    { id: 1, format: "pdf", label: "PDF", extension: ".pdf", defaultActive: true },
    { id: 3, format: "xlsx", label: "Excel", extension: ".xlsx", defaultActive: false },
    { id: 5, format: "docx", label: "Word", extension: ".docx", defaultActive: false },
];

/** Accepted extensions, in display order. */
export const ACCEPTED_UPLOAD_EXTENSIONS = [".pdf", ".xls", ".xlsx", ".doc", ".docx"] as const;

/** Human-readable accepted-formats list for error messages and UI copy. */
export const ACCEPTED_UPLOAD_FORMATS_LABEL = ACCEPTED_UPLOAD_EXTENSIONS.join(", ");

const EXTENSION_TO_FORMAT: Record<string, UploadFileFormat> = {
    ".pdf": "pdf",
    ".xls": "xls",
    ".xlsx": "xlsx",
    ".doc": "doc",
    ".docx": "docx",
};

const MIME_TO_FORMAT: Record<string, UploadFileFormat> = {
    "application/pdf": "pdf",
    "application/x-pdf": "pdf",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

// Macro-enabled Office formats are blocked by product decision (mirrors backend)
const BLOCKED_MACRO_EXTENSIONS = new Set([".xlsm", ".docm", ".xltm", ".dotm"]);

const BLOCKED_MACRO_MIMES = new Set([
    "application/vnd.ms-excel.sheet.macroenabled.12",
    "application/vnd.ms-excel.sheet.binary.macroenabled.12",
    "application/vnd.ms-excel.template.macroenabled.12",
    "application/vnd.ms-word.document.macroenabled.12",
    "application/vnd.ms-word.template.macroenabled.12",
]);

/** Value for `<input accept>` covering all accepted extensions + MIME types. */
export const UPLOAD_ACCEPT_ATTRIBUTE = [...ACCEPTED_UPLOAD_EXTENSIONS, ...Object.keys(MIME_TO_FORMAT)].join(",");

/** Extensions per format, derived from the extension map. */
const FORMAT_TO_EXTENSIONS: Record<UploadFileFormat, string[]> = Object.entries(EXTENSION_TO_FORMAT).reduce(
    (acc, [ext, format]) => {
        acc[format].push(ext);
        return acc;
    },
    { pdf: [], xls: [], xlsx: [], doc: [], docx: [] } as Record<UploadFileFormat, string[]>,
);

/** MIME types per format, derived from the MIME map. */
const FORMAT_TO_MIMES: Record<UploadFileFormat, string[]> = Object.entries(MIME_TO_FORMAT).reduce(
    (acc, [mime, format]) => {
        acc[format].push(mime);
        return acc;
    },
    { pdf: [], xls: [], xlsx: [], doc: [], docx: [] } as Record<UploadFileFormat, string[]>,
);

/** Build an `<input accept>` value covering only the given formats. */
export function buildAcceptAttribute(formats: ReadonlySet<UploadFileFormat>): string {
    const parts: string[] = [];
    for (const format of formats) {
        parts.push(...FORMAT_TO_EXTENSIONS[format], ...FORMAT_TO_MIMES[format]);
    }
    return parts.join(",");
}

/** Human-readable extension list for the given formats (e.g. ".pdf, .xlsx"). */
export function formatListLabel(formats: ReadonlySet<UploadFileFormat>): string {
    return ACCEPTED_UPLOAD_EXTENSIONS.filter((ext) => formats.has(EXTENSION_TO_FORMAT[ext])).join(", ");
}

function getExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : "";
}

/**
 * Classify a file for upload: accepted (with canonical format) or rejected
 * with a reason. Extension decides the format when recognized; MIME is the
 * fallback. Macro-enabled Office files (.xlsm/.docm) are explicitly blocked.
 * When `allowedFormats` is provided (the org's enabled file types), a
 * recognized format outside the set is rejected with "not-enabled-for-org".
 */
export function classifyUploadFile(file: File, allowedFormats?: ReadonlySet<UploadFileFormat>): UploadFileClassification {
    const extension = getExtension(file.name ?? "");
    const mime = (file.type ?? "").split(";")[0].trim().toLowerCase();

    if (BLOCKED_MACRO_EXTENSIONS.has(extension) || BLOCKED_MACRO_MIMES.has(mime)) {
        return { accepted: false, reason: "blocked-macro-enabled" };
    }

    const format = EXTENSION_TO_FORMAT[extension] ?? MIME_TO_FORMAT[mime];
    if (!format) {
        return { accepted: false, reason: "unsupported-type" };
    }

    if (allowedFormats && !allowedFormats.has(format)) {
        return { accepted: false, reason: "not-enabled-for-org", format };
    }

    return { accepted: true, format, isOffice: format !== "pdf" };
}
